import { useId, useState } from 'react';
import CountedField from './CountedField';
import { SERVICE_TEXT_LIMITS, hasErrorUnder, type FieldErrors } from './servicePageDraft';

/**
 * Spec 081a — per-option ("variation") texts in wizard step 2: "+ Add description" (≤ 300) and
 * "+ Add more details" (≤ 1500), each an EN/AR pair behind its own expander. An expander starts
 * open when it already holds text, and is forced open while it carries a server error.
 */
export interface VariationTextsValue {
  description: string;
  descriptionAr: string;
  moreDetails: string;
  moreDetailsAr: string;
}

export interface VariationTextsEditorProps {
  optionName: string;
  value: VariationTextsValue;
  onChange: (patch: Partial<VariationTextsValue>) => void;
  /** e.g. `variations[2]` — errors are read from `${prefix}.description` and the like. */
  errorPrefix: string;
  errors: FieldErrors;
}

export default function VariationTextsEditor({ optionName, value, onChange, errorPrefix, errors }: VariationTextsEditorProps) {
  const id = useId();
  const [descOpen, setDescOpen] = useState(() => !!(value.description || value.descriptionAr));
  const [moreOpen, setMoreOpen] = useState(() => !!(value.moreDetails || value.moreDetailsAr));
  const descHasError = hasErrorUnder(errors, `${errorPrefix}.description`) || hasErrorUnder(errors, `${errorPrefix}.descriptionAr`);
  const moreHasError = hasErrorUnder(errors, `${errorPrefix}.moreDetails`) || hasErrorUnder(errors, `${errorPrefix}.moreDetailsAr`);
  const showDesc = descOpen || descHasError;
  const showMore = moreOpen || moreHasError;
  const name = optionName.trim() || 'this option';
  const L = SERVICE_TEXT_LIMITS;

  return (
    <div className="svc-option-texts">
      <div className="svc-option-texts__toggles">
        <button type="button" className="svc-expander-btn" aria-expanded={showDesc} aria-controls={`${id}-desc`}
          onClick={() => setDescOpen((o) => !o)}>
          {showDesc ? '− Hide description' : (value.description || value.descriptionAr ? '▸ Description' : '+ Add description')}
        </button>
        <button type="button" className="svc-expander-btn" aria-expanded={showMore} aria-controls={`${id}-more`}
          onClick={() => setMoreOpen((o) => !o)}>
          {showMore ? '− Hide more details' : (value.moreDetails || value.moreDetailsAr ? '▸ More details' : '+ Add more details')}
        </button>
      </div>
      {showDesc && (
        <div id={`${id}-desc`} className="svc-page__pair svc-option-texts__panel">
          <CountedField label={`Description of ${name} (English)`} lang="en" multiline rows={2} value={value.description}
            maxLength={L.optionDescription} error={errors[`${errorPrefix}.description`]}
            onChange={(v) => onChange({ description: v })} testId="option-description" />
          <CountedField label={`Description of ${name} (Arabic)`} lang="ar" multiline rows={2} value={value.descriptionAr}
            maxLength={L.optionDescription} error={errors[`${errorPrefix}.descriptionAr`]}
            onChange={(v) => onChange({ descriptionAr: v })} testId="option-description-ar" />
        </div>
      )}
      {showMore && (
        <div id={`${id}-more`} className="svc-page__pair svc-option-texts__panel">
          <CountedField label={`More details on ${name} (English)`} lang="en" multiline rows={3} value={value.moreDetails}
            maxLength={L.optionMoreDetails} error={errors[`${errorPrefix}.moreDetails`]}
            onChange={(v) => onChange({ moreDetails: v })} testId="option-more-details" />
          <CountedField label={`More details on ${name} (Arabic)`} lang="ar" multiline rows={3} value={value.moreDetailsAr}
            maxLength={L.optionMoreDetails} error={errors[`${errorPrefix}.moreDetailsAr`]}
            onChange={(v) => onChange({ moreDetailsAr: v })} testId="option-more-details-ar" />
        </div>
      )}
    </div>
  );
}
